<template>
  <div class="field-survey-form-root">
    <div class="field-survey-form-inner">
      <header class="fsf-page-header">
        <h1 class="fsf-page-title">
          <template v-if="fieldSurveyLoading">{{ $t('FieldSurveyForm.loadingTitle') }}</template>
          <template v-else-if="fieldSurveyError">{{ $t('FieldSurveyForm.titleError') }}</template>
          <template v-else>{{ pageTitle }}</template>
        </h1>
      </header>

      <div class="fsf-main-card">
        <section class="fsf-map-panel" aria-label="field-survey-map">
          <field-survey-map
            v-if="!fieldSurveyLoading && !fieldSurveyError && fieldSurveyId"
            ref="fieldSurveyMap"
            class="fsf-map-embed"
            :survey-works="surveyWorks"
            :devices-qty-number="devicesQtyNumber"
            :disabled="surveyWorksLoading || !isSetupEditable"
            :saving="mapSaving"
            :technician-items="technicianItems"
            :technician-options-loading="technicianOptionsLoading"
            :field-survey-id="fieldSurveyId"
            @create-work="onCreateSurveyWorkFromMap"
            @add-blocked="onMapAddBlocked"
            @technician-user-created="onTechnicianUserCreated"
          />
          <div v-else class="fsf-map-placeholder" />
          <div class="fsf-map-hint-banner" role="status">
            <v-icon small class="fsf-map-hint-icon">mdi-information</v-icon>
            <span>{{ mapHintText }}</span>
          </div>
        </section>

        <aside class="fsf-sidebar">
          <div class="fsf-sidebar-header">
            <span class="fsf-sidebar-count">{{ hiredEquipmentsText }}</span>
            <a
              v-if="showHireMoreLink"
              class="fsf-hire-more-link"
              :href="hireMoreWhatsappUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('FieldSurveyForm.hireMore') }}
            </a>
          </div>

          <div v-if="showEditServiceLink" class="fsf-edit-service-wrap">
            <a
              class="fsf-edit-service-link"
              href="#"
              @click.prevent="enableAdminEdit"
            >
              {{ $t('FieldSurveyForm.editService') }}
            </a>
          </div>

          <div v-if="surveyWorksLoading" class="fsf-sidebar-loading py-6 text-center">
            <v-progress-circular indeterminate color="#0091d5" size="32" />
          </div>
          <div v-else-if="surveyWorksError" class="fsf-sidebar-error caption grey--text px-2">
            {{ $t('FieldSurveyForm.surveyWorksError') }}
          </div>
          <div v-else class="fsf-sidebar-list">
            <survey-work-card
              v-for="(work, index) in surveyWorks"
              :key="work.id"
              :work="work"
              :marker-letter="markerLetter(index)"
              :saving="savingId === work.id"
              :deleting="deletingId === work.id"
              :technician-items="technicianItems"
              :technician-options-loading="technicianOptionsLoading"
              :field-survey-id="fieldSurveyId"
              :readonly="!isSetupEditable"
              @save="onSaveWork"
              @delete="onDeleteWork"
              @technician-user-created="onTechnicianUserCreated"
            />
            <p v-if="!surveyWorks.length" class="fsf-empty caption grey--text px-2">
              {{ $t('FieldSurveyForm.noSurveyWorks') }}
            </p>
          </div>

          <div v-if="showConfirmButton" class="fsf-confirm-wrap">
            <v-btn
              class="fsf-confirm-btn"
              block
              large
              depressed
              :disabled="!canConfirm"
              @click="onConfirmClick"
            >
              {{ $t('FieldSurveyForm.confirm') }}
            </v-btn>
          </div>
          <div v-else-if="showFinishEditButton" class="fsf-confirm-wrap">
            <v-btn
              class="fsf-confirm-btn"
              block
              large
              depressed
              @click="finishAdminEdit"
            >
              {{ $t('FieldSurveyForm.finishEditService') }}
            </v-btn>
          </div>
        </aside>
      </div>
    </div>

    <v-dialog
      v-model="ponceChargeConfirmDialog"
      max-width="520"
      content-class="fsf-ponce-dialog-overlay"
      @click:outside="ponceChargeConfirmDialog = false"
    >
      <v-card class="fsf-ponce-dialog-card" flat>
        <v-btn
          icon
          class="fsf-ponce-close"
          :aria-label="$t('FieldSurveyForm.cancel')"
          @click="ponceChargeConfirmDialog = false"
        >
          <v-icon color="grey darken-2">mdi-close</v-icon>
        </v-btn>
        <v-card-text class="fsf-ponce-dialog-text pt-10 pb-2 text-center">
          {{ $t('FieldSurveyForm.ponceChargeWarningBody') }}
        </v-card-text>
        <v-card-actions class="fsf-ponce-dialog-actions px-4 pb-5 pt-2">
          <v-btn
            outlined
            depressed
            class="fsf-ponce-btn-cancel"
            @click="ponceChargeConfirmDialog = false"
          >
            {{ $t('FieldSurveyForm.cancel') }}
          </v-btn>
          <v-btn
            color="#0091d5"
            dark
            depressed
            class="fsf-ponce-btn-ok"
            @click="onConfirmPonceCharges"
          >
            {{ $t('FieldSurveyForm.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" :color="snackbar.color" top>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { firestore, firestoreConstructors } from '@/plugins/firebase'
import { markFieldSurveyInProgressForSetup, FIELD_SURVEY_STATUS } from '@/lib/fieldSurveysRouting'
import {
  uploadSurveyWorkPluviometricChart,
  deleteSurveyWorkPluviometricChartAtPath
} from '@/lib/surveyWorkPluviometricChart'
import {
  parseSurveyWorkUserRefId,
  fetchUsersByIds,
  formatTechnicianDisplayName,
  loadTechnicianSelectOptions,
  mergeTechnicianOption
} from '@/lib/surveyWorkTechnicians'
import { SURVEY_WORK_STATUS } from '@/lib/surveyWorkStatus'
import { trackEvent } from '@/lib/track-events'
import { EVENTS_TO_TRACK } from '@/constants/const'
import SurveyWorkCard from './SurveyWorkCard.vue'
import FieldSurveyMap from './FieldSurveyMap.vue'

const FIELD_SURVEYS = 'app/v1/fieldSurveys'
const SURVEY_WORKS = 'app/v1/surveyWorks'

function firestoreDateToJs (v) {
  if (!v) return null
  if (typeof v.toDate === 'function') return v.toDate()
  if (v instanceof Date) return v
  return null
}

export default {
  name: 'FieldSurveyForm',
  components: { SurveyWorkCard, FieldSurveyMap },
  data () {
    return {
      fieldSurveyLoading: true,
      fieldSurveyError: false,
      fieldSurveyCreatedAt: null,
      fieldSurveyDealId: '',
      fieldSurveyStatus: '',
      devicesQty: null,
      adminEditMode: false,
      surveyWorksLoading: true,
      surveyWorksError: false,
      surveyWorks: [],
      unsubscribeSurveyWorks: null,
      mapSaving: false,
      savingId: null,
      deletingId: null,
      technicianItems: [],
      technicianOptionsLoading: false,
      ponceChargeConfirmDialog: false,
      snackbar: {
        show: false,
        text: '',
        timeout: 4000,
        color: 'success'
      }
    }
  },
  computed: {
    fieldSurveyId () {
      return this.$route.params.id
    },
    orgName () {
      const o = this.$store.getters.userOrganization
      return (o && o.name) ? o.name : ''
    },
    organizationId () {
      const o = this.$store.getters.userOrganization
      return (o && o.id) ? o.id : ''
    },
    pageTitle () {
      const dateStr = this.formatFieldSurveyDate(this.fieldSurveyCreatedAt)
      return this.$t('FieldSurveyForm.pageTitle', {
        org: this.orgName || '—',
        date: dateStr || '—'
      })
    },
    devicesQtyNumber () {
      const q = this.devicesQty
      if (q === null || q === undefined) return null
      const n = Number(q)
      return Number.isFinite(n) ? n : null
    },
    hiredEquipmentsText () {
      const q = this.devicesQtyNumber
      if (q === null) {
        const n = this.surveyWorks.length
        if (n === 1) return this.$t('FieldSurveyForm.hiredEquipmentsUnknownOne')
        return this.$t('FieldSurveyForm.hiredEquipmentsUnknown', { count: n })
      }
      if (q === 1) {
        return this.$t('FieldSurveyForm.hiredEquipmentsOne')
      }
      return this.$t('FieldSurveyForm.hiredEquipmentsMany', { count: q })
    },
    mapHintText () {
      const q = this.devicesQtyNumber
      const next = Math.min((this.surveyWorks.length || 0) + 1, q || (this.surveyWorks.length + 1))
      return this.$t('FieldSurveyForm.mapHint', { equipmentNumber: next })
    },
    canConfirm () {
      if (this.fieldSurveyLoading || this.surveyWorksLoading || this.fieldSurveyError) return false
      const q = this.devicesQtyNumber
      if (q === null) return false
      return this.surveyWorks.length === q
    },
    hireMoreWhatsappUrl () {
      const raw = process.env.VUE_APP_ESTEBAN_PHONE
      if (raw === undefined || raw === null || raw === '') return ''
      const digits = String(raw).replace(/\D/g, '')
      if (!digits) return ''
      const text = encodeURIComponent(this.$t('FieldSurveyForm.hireMoreWhatsappText'))
      return `https://wa.me/${digits}?text=${text}`
    },
    showHireMoreLink () {
      return !this.isFieldSurveyCompleted && this.canConfirm && !!this.hireMoreWhatsappUrl
    },
    hasAnyPonceTechnicianSurveyWork () {
      return this.surveyWorks.some((w) => w.ponceTechnician)
    },
    isPonceAdmin () {
      if (this.$store.getters.impersonatedUserId) return true
      const user = this.$store.getters.user
      return !!(user && user.isAdmin)
    },
    isFieldSurveyCompleted () {
      return this.fieldSurveyStatus === FIELD_SURVEY_STATUS.COMPLETED
    },
    isSetupEditable () {
      return !this.isFieldSurveyCompleted || (this.isPonceAdmin && this.adminEditMode)
    },
    showEditServiceLink () {
      return this.isFieldSurveyCompleted && this.isPonceAdmin && !this.adminEditMode
    },
    showFinishEditButton () {
      return this.isFieldSurveyCompleted && this.isPonceAdmin && this.adminEditMode
    },
    showConfirmButton () {
      return !this.isFieldSurveyCompleted
    }
  },
  watch: {
    fieldSurveyId: {
      immediate: true,
      handler (id) {
        this.adminEditMode = false
        this.teardownSurveyWorksListener()
        if (id) this.bootstrap(id)
      }
    }
  },
  beforeDestroy () {
    this.teardownSurveyWorksListener()
  },
  methods: {
    formatFieldSurveyDate (createdAt) {
      const d = firestoreDateToJs(createdAt)
      if (!d) return ''
      const loc = this.$i18n.locale === 'es' ? 'es-AR' : 'en-US'
      return d.toLocaleDateString(loc, { month: 'short', day: 'numeric' })
    },
    markerLetter (index) {
      return String.fromCharCode(65 + (index % 26))
    },
    showSnackbar (text, color = 'success') {
      this.snackbar = { ...this.snackbar, show: true, text, color }
    },
    teardownSurveyWorksListener () {
      if (this.unsubscribeSurveyWorks) {
        this.unsubscribeSurveyWorks()
        this.unsubscribeSurveyWorks = null
      }
    },
    async bootstrap (fieldSurveyId) {
      this.fieldSurveyLoading = true
      this.fieldSurveyError = false
      this.surveyWorksLoading = true
      this.surveyWorksError = false
      this.surveyWorks = []
      this.technicianItems = []
      const user = this.$store.getters.user
      const allowedIds = (user && user.fieldSurveyIds) || []
      const isAdmin = !!(user && user.isAdmin)
      if (!allowedIds.includes(fieldSurveyId) && !isAdmin) {
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_FORM_ACCESS_DENIED, { fieldSurveyId })
        this.fieldSurveyLoading = false
        this.surveyWorksLoading = false
        this.fieldSurveyError = true
        return
      }
      try {
        const db = await firestore
        const ref = db.collection(FIELD_SURVEYS).doc(fieldSurveyId)
        const snap = await ref.get()
        if (!snap.exists) {
          trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_FORM_NOT_FOUND, { fieldSurveyId })
          this.fieldSurveyError = true
          this.fieldSurveyLoading = false
          this.surveyWorksLoading = false
          return
        }
        const data = snap.data() || {}
        this.fieldSurveyStatus = data.status != null ? String(data.status) : FIELD_SURVEY_STATUS.NEW
        this.fieldSurveyCreatedAt = data.createdAt
        this.fieldSurveyDealId = data.dealId != null ? String(data.dealId) : ''
        this.devicesQty = data.devicesQty

        if (this.fieldSurveyStatus === FIELD_SURVEY_STATUS.COMPLETED && !this.isPonceAdmin) {
          this.fieldSurveyLoading = false
          this.surveyWorksLoading = false
          const query = this.fieldSurveyDealId ? { dealId: this.fieldSurveyDealId } : {}
          this.$router.replace({ path: '/audits', query })
          return
        }

        if (
          !this.$store.getters.impersonatedUserId &&
          this.fieldSurveyStatus === FIELD_SURVEY_STATUS.NEW
        ) {
          try {
            await markFieldSurveyInProgressForSetup(db, fieldSurveyId)
            this.fieldSurveyStatus = FIELD_SURVEY_STATUS.IN_PROGRESS
          } catch (e) {
            console.error('Field survey status update failed:', e)
          }
        }
        this.fieldSurveyLoading = false
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_FORM_LOADED, {
          fieldSurveyId,
          fieldSurveyDealId: this.fieldSurveyDealId,
          devicesQty: this.devicesQtyNumber
        })

        const q = db.collection(SURVEY_WORKS).where('fieldSurvey', '==', ref)
        this.unsubscribeSurveyWorks = q.onSnapshot(
          (querySnap) => {
            const list = []
            querySnap.forEach((doc) => {
              const d = doc.data() || {}
              const ponceTechnician = !!d.ponceTechnician
              const userId = ponceTechnician ? '' : (parseSurveyWorkUserRefId(d.user) || '')
              list.push({
                id: doc.id,
                deviceName: d.deviceName != null ? String(d.deviceName) : (d.name != null ? String(d.name) : ''),
                userId,
                ponceTechnician,
                userDisplay: '',
                coordinates: d.coordinates != null ? String(d.coordinates) : '',
                pluviometricChart: d.pluviometricChart != null ? String(d.pluviometricChart) : '',
                status: d.status != null ? String(d.status) : '',
                createdAt: d.createdAt
              })
            })
            list.sort((a, b) => {
              const ta = firestoreDateToJs(a.createdAt)
              const tb = firestoreDateToJs(b.createdAt)
              if (ta && tb) return ta.getTime() - tb.getTime()
              if (ta && !tb) return -1
              if (!ta && tb) return 1
              return a.id.localeCompare(b.id)
            })
            ;(async () => {
              try {
                const dbInner = await firestore
                const refIds = list.filter((r) => r.userId && !r.ponceTechnician).map((r) => r.userId)
                const userMap = await fetchUsersByIds(dbInner, refIds)
                list.forEach((r) => {
                  if (r.ponceTechnician) {
                    r.userDisplay = this.$t('FieldSurveyForm.ponceTechnicianOption')
                  } else if (r.userId && userMap.has(r.userId)) {
                    const u = userMap.get(r.userId)
                    r.userDisplay = formatTechnicianDisplayName(u.name, u.lastName)
                  } else if (r.userId) {
                    r.userDisplay = r.userId
                  } else {
                    r.userDisplay = ''
                  }
                })
                this.surveyWorks = list
                this.surveyWorksLoading = false
                this.surveyWorksError = false
              } catch (enrichErr) {
                console.error('surveyWorks enrich error:', enrichErr)
                list.forEach((r) => {
                  if (r.ponceTechnician) {
                    r.userDisplay = this.$t('FieldSurveyForm.ponceTechnicianOption')
                  }
                })
                this.surveyWorks = list
                this.surveyWorksLoading = false
                this.surveyWorksError = false
              }
            })()
          },
          (err) => {
            console.error('surveyWorks listener error:', err)
            this.surveyWorksError = true
            this.surveyWorksLoading = false
          }
        )
        this.loadTechnicianOptions()
      } catch (e) {
        console.error('FieldSurveyForm bootstrap:', e)
        this.fieldSurveyError = true
        this.fieldSurveyLoading = false
        this.surveyWorksLoading = false
      }
    },
    async onSaveWork ({ id, deviceName, userId, ponceTechnician, coordinates, pluviometricChartChange, pluviometricChartFile }) {
      if (!this.isSetupEditable) return
      this.savingId = id
      try {
        const db = await firestore
        const work = this.surveyWorks.find((w) => w.id === id)
        const previousPath = work && work.pluviometricChart ? String(work.pluviometricChart).trim() : ''

        let nextChartPath = previousPath
        if (pluviometricChartChange === 'replace' && pluviometricChartFile) {
          const orgName = this.$store.getters.userOrganization.name || ''
          nextChartPath = await uploadSurveyWorkPluviometricChart(pluviometricChartFile, orgName, id)
        } else if (pluviometricChartChange === 'remove') {
          nextChartPath = ''
        }

        const uid = String(userId || '').trim()
        const payload = {
          deviceName,
          coordinates,
          updatedAt: firestoreConstructors.FieldValue.serverTimestamp()
        }
        if (ponceTechnician) {
          payload.user = null
          payload.ponceTechnician = true
        } else {
          payload.ponceTechnician = false
          if (uid) {
            payload.user = db.collection('app/v1/users').doc(uid)
          } else {
            payload.user = null
          }
        }
        if (pluviometricChartChange !== 'none') {
          payload.pluviometricChart = nextChartPath
        }

        if (work && String(work.status || '').trim() === '') {
          payload.status = SURVEY_WORK_STATUS.NEW
        }

        await db.collection(SURVEY_WORKS).doc(id).update(payload)

        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_SURVEY_WORK_SAVED, {
          fieldSurveyId: this.fieldSurveyId,
          surveyWorkId: id,
          pluviometricChartChange,
          ponceTechnician: !!ponceTechnician
        })

        if (pluviometricChartChange === 'replace' && pluviometricChartFile && previousPath && previousPath !== nextChartPath) {
          await deleteSurveyWorkPluviometricChartAtPath(previousPath)
        }
        if (pluviometricChartChange === 'remove' && previousPath) {
          await deleteSurveyWorkPluviometricChartAtPath(previousPath)
        }
        this.showSnackbar(this.$t('FieldSurveyForm.saveSuccess'))
      } catch (e) {
        console.error('SurveyWork update failed:', e)
        this.showSnackbar(this.$t('FieldSurveyForm.saveError'), 'error')
      } finally {
        this.savingId = null
      }
    },
    async onDeleteWork (id) {
      if (!this.isSetupEditable) return
      this.deletingId = id
      try {
        const db = await firestore
        const work = this.surveyWorks.find((w) => w.id === id)
        const chartPath = work && work.pluviometricChart ? String(work.pluviometricChart).trim() : ''
        if (chartPath) {
          await deleteSurveyWorkPluviometricChartAtPath(chartPath)
        }
        await db.collection(SURVEY_WORKS).doc(id).delete()
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_SURVEY_WORK_DELETED, {
          fieldSurveyId: this.fieldSurveyId,
          surveyWorkId: id
        })
        this.showSnackbar(this.$t('FieldSurveyForm.deleteSuccess'))
      } catch (e) {
        console.error('SurveyWork delete failed:', e)
        this.showSnackbar(this.$t('FieldSurveyForm.deleteError'), 'error')
      } finally {
        this.deletingId = null
      }
    },
    onConfirmClick () {
      if (!this.canConfirm) return
      if (this.hasAnyPonceTechnicianSurveyWork) {
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_PONCE_CHARGE_DIALOG_OPENED, {
          fieldSurveyId: this.fieldSurveyId
        })
        this.ponceChargeConfirmDialog = true
        return
      }
      this.runFieldSurveyConfirmFlow()
    },
    onConfirmPonceCharges () {
      this.ponceChargeConfirmDialog = false
      this.runFieldSurveyConfirmFlow()
    },
    async runFieldSurveyConfirmFlow () {
      if (!this.fieldSurveyId || !this.canConfirm) return
      this.mapSaving = true
      try {
        const db = await firestore
        await db.collection(FIELD_SURVEYS).doc(this.fieldSurveyId).update({
          status: 'completed',
          updatedAt: firestoreConstructors.FieldValue.serverTimestamp()
        })
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_COMPLETED, {
          fieldSurveyId: this.fieldSurveyId,
          fieldSurveyDealId: this.fieldSurveyDealId
        })
        this.showSnackbar(this.$t('FieldSurveyForm.confirmSuccess'))
        const query = this.fieldSurveyDealId ? { dealId: this.fieldSurveyDealId } : {}
        this.$router.push({ path: '/audits', query })
      } catch (e) {
        console.error('Field survey confirm failed:', e)
        this.showSnackbar(this.$t('FieldSurveyForm.confirmError'), 'error')
      } finally {
        this.mapSaving = false
      }
    },
    onTechnicianUserCreated ({ id, name, lastName }) {
      trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_TECHNICIAN_USER_CREATED, {
        fieldSurveyId: this.fieldSurveyId,
        newUserId: id
      })
      this.technicianItems = mergeTechnicianOption(this.technicianItems, id, name, lastName)
    },
    async loadTechnicianOptions () {
      const orgId = this.organizationId
      if (!orgId || !this.fieldSurveyId) return
      this.technicianOptionsLoading = true
      try {
        const db = await firestore
        const fieldSurveyRef = db.collection(FIELD_SURVEYS).doc(this.fieldSurveyId)
        const items = await loadTechnicianSelectOptions(db, orgId, fieldSurveyRef)
        this.technicianItems = items
      } catch (e) {
        console.error('loadTechnicianOptions:', e)
      } finally {
        this.technicianOptionsLoading = false
      }
    },
    enableAdminEdit () {
      this.adminEditMode = true
    },
    finishAdminEdit () {
      this.adminEditMode = false
      const query = this.fieldSurveyDealId ? { dealId: this.fieldSurveyDealId } : {}
      this.$router.push({ path: '/audits', query })
    },
    onMapAddBlocked () {
      const q = this.devicesQtyNumber
      const reason = q === null ? 'unknown_qty' : 'at_limit'
      trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_MAP_ADD_BLOCKED, {
        fieldSurveyId: this.fieldSurveyId,
        reason
      })
      if (q === null) {
        this.showSnackbar(this.$t('FieldSurveyForm.mapAddBlockedNoQty'), 'info')
      } else {
        this.showSnackbar(this.$t('FieldSurveyForm.mapAddBlockedLimit'), 'info')
      }
    },
    async onCreateSurveyWorkFromMap ({ deviceName, userId, ponceTechnician, coordinates, pluviometricChartFile }) {
      if (!this.fieldSurveyId || !this.isSetupEditable) return
      this.mapSaving = true
      try {
        const db = await firestore
        const uid = String(userId || '').trim()
        const payload = {
          fieldSurvey: db.collection(FIELD_SURVEYS).doc(this.fieldSurveyId),
          deviceName,
          coordinates,
          status: SURVEY_WORK_STATUS.NEW,
          createdAt: firestoreConstructors.FieldValue.serverTimestamp(),
          updatedAt: firestoreConstructors.FieldValue.serverTimestamp()
        }
        if (ponceTechnician) {
          payload.user = null
          payload.ponceTechnician = true
        } else {
          payload.ponceTechnician = false
          payload.user = uid ? db.collection('app/v1/users').doc(uid) : null
        }
        const createdRef = await db.collection(SURVEY_WORKS).add(payload)

        if (pluviometricChartFile) {
          const orgName = this.$store.getters.userOrganization.name || ''
          const pluviometricChart = await uploadSurveyWorkPluviometricChart(
            pluviometricChartFile,
            orgName,
            createdRef.id
          )
          await createdRef.update({
            pluviometricChart,
            updatedAt: firestoreConstructors.FieldValue.serverTimestamp()
          })
        }
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_SURVEY_WORK_CREATED_FROM_MAP, {
          fieldSurveyId: this.fieldSurveyId,
          surveyWorkId: createdRef.id,
          ponceTechnician: !!ponceTechnician
        })
        this.showSnackbar(this.$t('FieldSurveyForm.saveSuccess'))
        if (this.$refs.fieldSurveyMap) {
          this.$refs.fieldSurveyMap.notifyCreateSuccess()
        }
      } catch (e) {
        console.error('SurveyWork create failed:', e)
        this.showSnackbar(this.$t('FieldSurveyForm.saveError'), 'error')
      } finally {
        this.mapSaving = false
      }
    }
  }
}
</script>

<style scoped lang="stylus">
$fsf-bg = #f4f7f9
$fsf-primary = #0098E1
$fsf-text = #333333
$fsf-text-secondary = #666666
$fsf-hint-bg = #ffcc00
$fsf-card-radius = 12px

.field-survey-form-root
  /* Fill v-main (already sized below toolbar when toolbar is visible) */
  height 100%
  max-height 100%
  min-height 0
  background $fsf-bg
  padding 12px 16px
  box-sizing border-box
  display flex
  flex-direction column
  overflow hidden

.field-survey-form-inner
  flex 1 1 auto
  min-height 0
  display flex
  flex-direction column
  width 100%
  max-width 1320px
  margin 0 auto

.fsf-page-header
  flex-shrink 0
  margin-bottom 12px

.fsf-page-title
  font-size 1.5rem
  font-weight 500
  color $fsf-text
  line-height 1.3
  margin 0
  letter-spacing -0.02em

@media (min-width: 960px)
  .fsf-page-title
    font-size 1.65rem

.fsf-main-card
  flex 1 1 auto
  min-height 0
  display flex
  flex-direction column
  background #fff
  border-radius $fsf-card-radius
  box-shadow 0 1px 4px rgba(0, 32, 47, 0.08)
  overflow hidden

@media (min-width: 960px)
  .fsf-main-card
    flex-direction row
    align-items stretch

.fsf-map-panel
  position relative
  flex 1 1 auto
  min-height 0
  background #e8ecef

@media (max-width: 959px)
  .fsf-map-panel
    min-height 240px
    flex 1 1 45%

@media (min-width: 960px)
  .fsf-map-panel
    flex 2 1 0
    min-height 0
    min-width 0

.fsf-map-placeholder
  position absolute
  inset 0
  background linear-gradient(160deg, #dfe6ea 0%, #eef2f5 45%, #e4e9ed 100%)

.fsf-map-embed
  position absolute
  inset 0

.fsf-map-hint-banner
  position absolute
  left 50%
  bottom 20px
  transform translateX(-50%)
  z-index 2
  display flex
  align-items center
  gap 8px
  max-width calc(100% - 32px)
  padding 10px 18px
  background $fsf-hint-bg
  border-radius 999px
  font-size 0.8125rem
  font-weight 500
  color #333
  box-shadow 0 2px 8px rgba(0, 0, 0, 0.12)

.fsf-map-hint-icon
  color #333 !important
  flex-shrink 0

.fsf-sidebar
  flex 1 1 auto
  min-height 0
  display flex
  flex-direction column
  padding 20px 20px 16px
  max-width 100%
  box-sizing border-box
  border-top 1px solid rgba(0, 32, 47, 0.06)
  overflow hidden

@media (min-width: 960px)
  .fsf-sidebar
    flex 1 1 0
    max-width 400px
    min-width 300px
    border-top none
    border-left 1px solid rgba(0, 32, 47, 0.06)

.fsf-sidebar-loading
.fsf-sidebar-error
  flex-shrink 0

.fsf-sidebar-header
  flex-shrink 0
  margin-bottom 16px
  font-size 0.9375rem
  color $fsf-text
  line-height 1.4
  display flex
  flex-wrap wrap
  align-items baseline
  column-gap 6px
  row-gap 4px

.fsf-sidebar-count
  font-weight 500

.fsf-hire-more-link
  display inline
  color $fsf-primary
  font-weight 500
  font-size 0.9375rem
  text-decoration none
  white-space nowrap

.fsf-hire-more-link:hover
  text-decoration underline

.fsf-edit-service-wrap
  flex-shrink 0
  display flex
  justify-content flex-end
  margin-bottom 12px

.fsf-edit-service-link
  color $fsf-primary
  font-weight 500
  font-size 0.9375rem
  text-decoration none
  white-space nowrap

.fsf-edit-service-link:hover
  text-decoration underline

.fsf-confirm-wrap
  flex-shrink 0
  margin-top auto
  padding-top 12px
  width 100%

.fsf-confirm-btn
  text-transform none
  font-weight 600
  font-size 1rem
  letter-spacing 0.02em
  min-height 48px !important
  height auto !important
  padding 12px 20px !important
  background $fsf-primary !important
  color #fff !important

.fsf-confirm-btn.v-btn--disabled
  background #e0e4e8 !important
  color #fff !important
  opacity 1

.fsf-sidebar-list
  flex 1 1 auto
  min-height 0
  overflow-x visible
  overflow-y auto
  -webkit-overflow-scrolling touch
  margin-bottom 16px
  padding 14px 2px 0 12px

.fsf-empty
  margin-top 8px

.fsf-ponce-dialog-card
  position relative
  border-radius 8px
  padding-top 4px

.fsf-ponce-close
  position absolute
  right 4px
  top 4px
  z-index 1

.fsf-ponce-dialog-text
  color #424242
  font-size 0.9375rem
  line-height 1.5
  padding-left 28px !important
  padding-right 28px !important

.fsf-ponce-dialog-actions
  flex-wrap wrap
  justify-content center

.fsf-ponce-btn-cancel
  border-color #e0e0e0 !important
  color #424242 !important
  text-transform none

.fsf-ponce-btn-ok
  text-transform none
  margin-left 12px !important
</style>



// WEBPACK FOOTER //
// FieldSurveyForm.vue?4c6b2164